import { capitalizeString } from '@zspace/format';
import {
  ALL_ZSPACE_STAFF_AND_PARTNER_PERMISSIONS_GROUPS,
  PermissionsGroupName,
  UserPermissions,
} from '@zspace/roles';
import {
  DeferredResponse,
  InputType,
  PaginatedAPIResponse,
  PaginatedContentConfig,
  SortDirection,
  User,
  UserStatus,
  UsersCriteria,
  UsersTableFilterDataType,
} from '@zspace/types';
import { Suspense, useCallback, useMemo, useState } from 'react';
import { Columns, Element, Icon } from 'react-bulma-components';
import { FaPlus } from 'react-icons/fa6';
import {
  LoaderFunction,
  defer,
  useAsyncValue,
  useLoaderData,
  useNavigate,
  useNavigation,
  useSearchParams,
} from 'react-router-dom';
import CheckPermissions from '../../shared/check-permissions/check-permissions';
import Conditional from '../../shared/conditional/conditional';
import ErrorHandlingAwait from '../../shared/error-handling-await/error-handling-await';
import FilterBar, { FiltersData } from '../../shared/filter-bar/filter-bar';
import useHttpRequest from '../../shared/hooks/http-request';
import useIsZspaceInternalUser from '../../shared/hooks/is-zspace-internal-user';
import useUpdateUrl from '../../shared/hooks/url';
import ProtectedPage from '../../shared/protected-page/protected-page';
import {
  createSearchParams,
  getSearchParamValue,
  getSearchParamValues,
} from '../../shared/url';
import BoxLayout from '../../ui/box-layout/box-layout';
import Button from '../../ui/button/button';
import PageSpinner from '../../ui/page-spinner/page-spinner';
import { OnTableChangeData } from '../../ui/table/types';
import { disableUser, editUserPermissions, fetchUsers } from '../users-service';
import UsersTable, {
  PermissionsGroupBySalesOrder,
} from '../users-table/users-table';

const UPDATE_USER_PERMISSIONS_ERROR_MESSAGE =
  'The user permissions could not be updated. Please try again';
const DISABLE_USER_ERROR_MESSAGE =
  'The user could not be disabled. Please try again';

export const loader: LoaderFunction = async ({ request }) => {
  const searchParams = createSearchParams(request);

  const response = fetchUsers({
    itemsPerPage: parseInt(
      getSearchParamValue(searchParams, 'itemsPerPage') ?? '50'
    ),
    pageNumber: parseInt(
      getSearchParamValue(searchParams, 'pageNumber') ?? '1'
    ),
    search: getSearchParamValue(searchParams, 'search') ?? '',
    sortBy: getSearchParamValue(searchParams, 'sortBy') ?? '',
    sortDirection: getSearchParamValue<SortDirection>(
      searchParams,
      'sortDirection'
    ),
    email: getSearchParamValues(searchParams, 'email'),
    permissionsGroup: getSearchParamValues(searchParams, 'permissionsGroup'),
    status: getSearchParamValues<UserStatus>(searchParams, 'status'),
  });

  return defer({ response });
};

const ITEMS_PER_PAGE_OPTIONS = [10, 20, 30, 50];

const initialCriteriaData: UsersCriteria = {
  itemsPerPage: 50,
  pageNumber: 1,
  search: '',
  sortBy: '',
  sortDirection: SortDirection.ASC,
};

export function UsersControlPanelPageContent() {
  const response = useAsyncValue() as PaginatedAPIResponse<User>;

  const [searchParams] = useSearchParams();
  const updateUrl = useUpdateUrl();
  const navigation = useNavigation();
  const navigate = useNavigate();
  const isZspaceInternalUser = useIsZspaceInternalUser();

  const [criteria, setCriteria] = useState<UsersCriteria>({
    itemsPerPage: parseInt(
      getSearchParamValue(searchParams, 'itemsPerPage') ?? '50'
    ),
    pageNumber: parseInt(
      getSearchParamValue(searchParams, 'pageNumber') ?? '1'
    ),
    search: getSearchParamValue(searchParams, 'search') ?? '',
    sortBy: getSearchParamValue(searchParams, 'sortBy') ?? '',
    sortDirection: getSearchParamValue<SortDirection>(
      searchParams,
      'sortDirection'
    ),
    email: getSearchParamValues(searchParams, 'email'),
    permissionsGroup: getSearchParamValues(searchParams, 'permissionsGroup'),
    status: getSearchParamValues<UserStatus>(searchParams, 'status'),
  });
  const { executeHttpRequest, isLoading } = useHttpRequest();

  const tableData: User[] = useMemo(() => response.data, [response.data]);

  const tableConfig: PaginatedContentConfig = useMemo(
    () => ({ ...criteria, pages: response.pages }),
    [criteria, response.pages]
  );

  const filtersData = useMemo(() => {
    return {
      email: {
        title: 'User',
        appliedCount: criteria.email?.length,
        value: criteria.email,
        input: {
          type: InputType.FILTER_CRITERIA,
          filterValueKey: 'email',
          valueKey: 'email',
          placeholder: 'Search by User',
        },
      },
      ...(isZspaceInternalUser && {
        permissionsGroup: {
          title: 'Role',
          appliedCount: criteria.permissionsGroup?.length,
          value: criteria.permissionsGroup,
          input: {
            type: InputType.SELECT,
            valueKey: 'permissionsGroup',
            options: ALL_ZSPACE_STAFF_AND_PARTNER_PERMISSIONS_GROUPS.map(
              (role) => ({
                label: role,
                value: role,
              })
            ),
          },
        },
      }),
      status: {
        title: 'Status',
        appliedCount: criteria.status?.length,
        value: criteria.status,
        input: {
          type: InputType.SELECT,
          valueKey: 'status',
          options: [UserStatus.ACTIVE, UserStatus.PENDING_FIRST_LOGIN].map(
            (status) => ({
              label: capitalizeString(status),
              value: status,
            })
          ),
        },
      },
    };
  }, [
    criteria.email,
    criteria.permissionsGroup,
    criteria.status,
    isZspaceInternalUser,
  ]) as FiltersData<UsersCriteria>;

  const appliedFilters = useMemo(() => {
    return Object.entries(filtersData).filter(
      ([_, value]) => value.appliedCount > 0
    );
  }, [filtersData]);

  const emptyContentText = useMemo(() => {
    const hasAppliedFilters =
      criteria.search !== '' || appliedFilters.length > 0;
    if (hasAppliedFilters) {
      return 'There are no users that match the selected criteria';
    }
    return 'There are no users at the moment';
  }, [appliedFilters.length, criteria.search]);

  const handleOnTableChange = useCallback(
    (value: OnTableChangeData<User, PermissionsGroupBySalesOrder>): void => {
      const newCriteria = {
        ...criteria,
        ...value.config,
        sortBy: value.column?.key || criteria.sortBy,
      };

      setCriteria(newCriteria);
      updateUrl(newCriteria);
    },
    [criteria, updateUrl]
  );

  const handleInviteUser = useCallback(() => {
    navigate('/users/panel/invite');
  }, [navigate]);

  const handleDisableUser = useCallback(
    async (id: string) =>
      executeHttpRequest({
        asyncFunction: async () => {
          await disableUser(id);
          setCriteria(initialCriteriaData);
          navigate('/users/panel');
        },
        customErrorMessage: DISABLE_USER_ERROR_MESSAGE,
      }),
    [executeHttpRequest, navigate]
  );

  const handleEditUserPermissions = useCallback(
    async (id: string, permissionsGroup?: PermissionsGroupName) =>
      executeHttpRequest({
        asyncFunction: async () => {
          await editUserPermissions(id, { permissionsGroup });
          setCriteria(initialCriteriaData);
          navigate(`/users/panel`);
        },
        customErrorMessage: UPDATE_USER_PERMISSIONS_ERROR_MESSAGE,
      }),
    [executeHttpRequest, navigate]
  );

  const handleTableFilterSubmit = useCallback(
    (tableFilterValue: UsersTableFilterDataType) => {
      const newCriteria = { ...criteria, ...tableFilterValue, pageNumber: 1 };
      setCriteria(newCriteria);
      updateUrl(newCriteria);
    },
    [criteria, updateUrl]
  );

  const onRemoveFilters = useCallback(
    (filterDataKeys: (keyof UsersCriteria)[]) => {
      const newCriteria = { ...criteria };
      for (const filterDataKey of filterDataKeys) {
        const initialFilterValue = initialCriteriaData[filterDataKey];
        if (initialFilterValue === undefined) {
          delete newCriteria[filterDataKey];
        } else {
          newCriteria[filterDataKey] = initialFilterValue as string &
            string[] &
            boolean;
        }
      }
      setCriteria(newCriteria);
      updateUrl(newCriteria);
    },
    [criteria, updateUrl]
  );

  const navigateToEditUserPermissions = useCallback(
    (user: User) => {
      navigate(`/users/${user.id}/edit`);
    },
    [navigate]
  );

  return (
    <BoxLayout
      className="is-min-height-80 mx-10 my-4"
      header={
        <Columns marginless>
          <Columns.Column className="pl-4">
            <h1 className="is-size-3 has-text-weight-light">
              User control panel
            </h1>
          </Columns.Column>
          <CheckPermissions permissions={UserPermissions.USERS_CREATE}>
            <CheckPermissions.Render>
              <Columns.Column
                display="flex"
                justifyContent="flex-end"
                className="gap-3"
              >
                <Button color="primary-dark" onClick={handleInviteUser}>
                  <Icon>
                    <FaPlus />
                  </Icon>
                  <span>New user</span>
                </Button>
              </Columns.Column>
            </CheckPermissions.Render>
          </CheckPermissions>
        </Columns>
      }
    >
      <Element>
        <Columns multiline={false}>
          <Columns.Column
            display="flex"
            alignItems="center"
            className="gap-5"
            size={11}
          >
            <Element display="flex" alignItems="center" className="gap-6">
              <span className="is-size-4 has-text-weight-light has-text-primary-dark">
                Internal users
              </span>
              <span className="is-size-6">{response.total} users</span>
            </Element>
          </Columns.Column>
        </Columns>

        <Conditional condition={navigation.state === 'loading'}>
          <Conditional.True>
            <PageSpinner />
          </Conditional.True>
          <Conditional.False>
            <Element display="flex" className="w-full mb-2">
              <FilterBar
                initialData={initialCriteriaData}
                data={criteria}
                filtersData={filtersData}
                onFilterSubmit={handleTableFilterSubmit}
                onRemoveFilter={onRemoveFilters}
              />
            </Element>
            <UsersTable
              dataSource={tableData}
              config={tableConfig}
              empty={
                <Element
                  display="flex"
                  justifyContent="center"
                  className="my-2 has-text-weight-light"
                >
                  <span>{emptyContentText}</span>
                </Element>
              }
              onChange={handleOnTableChange}
              form={{
                itemsPerPageOptions: ITEMS_PER_PAGE_OPTIONS,
                enableSearch: false,
              }}
              handleDisableUser={handleDisableUser}
              handleEditUserPermissions={handleEditUserPermissions}
              handleUserTemporaryPermissionsClick={
                navigateToEditUserPermissions
              }
              loading={isLoading}
            />
          </Conditional.False>
        </Conditional>
      </Element>
    </BoxLayout>
  );
}

export function UsersControlPanelPage() {
  const { response } = useLoaderData() as DeferredResponse<
    PaginatedAPIResponse<User>
  >;

  const Skeleton = () => (
    <BoxLayout
      className={`is-min-height-80 mx-10 my-4`}
      header={
        <Columns marginless>
          <Columns.Column className="pl-4">
            <h1 className="is-size-3 has-text-weight-light">
              User control panel
            </h1>
          </Columns.Column>
        </Columns>
      }
    >
      <PageSpinner />
    </BoxLayout>
  );

  return (
    <ProtectedPage
      permissions={UserPermissions.USERS_READ}
      allowedPermissionGroups={ALL_ZSPACE_STAFF_AND_PARTNER_PERMISSIONS_GROUPS}
    >
      <Suspense fallback={<Skeleton />}>
        <ErrorHandlingAwait resolve={response}>
          <UsersControlPanelPageContent />
        </ErrorHandlingAwait>
      </Suspense>
    </ProtectedPage>
  );
}

export default UsersControlPanelPage;
